// Components/Tabs/LabNavigationTab.jsx

import { ChartIcon, EspressoMachineIcon, WrenchIcon, ShapesIcon, UsersIcon, SettingsIcon, ShieldIcon, ListChecksIcon, SlidersIcon } from '../AdminIcon';
import React from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import { SliderBranch } from "../SliderBranch/SliderBranch.jsx";
import ManageUsersTab from "./ManageUsersTab.jsx";
import ManageMachinesTab from "./ManageMachinesTab.jsx";
import MachineTypesWarningTab from "./MachineTypesWarningTab.jsx";
import ToolCategoriesTab from "./ToolCategoriesTab.jsx";
import BadgeMachineTypeWarningTab from "./BadgeMachineTypeWarningTab.jsx";
import OtherSettingsTab from "./OtherSettingsTab.jsx";
import CustomReasonsTab from "./CustomReasonsTab";
import AdminToolsTab from "./AdminToolsTab.jsx";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * LabNavigationTab Component
 * 
 * This component serves as the main navigation hub for various administrative tasks
 * within a specific lab. Depending on the Badgr integration state (`badgrState`),
 * it renders different navigation options.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {string} props.badgrState - The current Badgr integration state ('True' or 'False').
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function LabNavigationTab({ user, labId, badgrState, pushTab, revert, onLabChanged }) {
  useDocumentTitle("Admin: Space Navigation Tab – Pinpoint");

  // Define navigation entries based on Badgr integration state
  const entries = badgrState === "True" ? [
    {
      // Option to manage space admins
      icon: UsersIcon,
      text: "Space admins",
      description: "Add or remove the people who can administer this space.",
      onClick: () => pushTab(
        <ManageUsersTab adminRoute="spaceAdmins"
          key={`manageUsers-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to manage machines
      icon: EspressoMachineIcon,
      text: "Tool instances",
      description: "Add and edit the individual tools in this space, including their names, tool definitions, and categories.",
      onClick: () => pushTab(
        <ManageMachinesTab adminRoute="instances"
          key={`manageMachines-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
          index = {2} // Index for stacking order
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to manage machine types with a warning
      icon: WrenchIcon,
      text: "Tools",
      description: "Add, rename, archive, and restore shared tool definitions. Changes can affect tool instances across spaces.",
      onClick: () => pushTab(
        <MachineTypesWarningTab adminRoute="toolsWarning"
          key={`machineTypesWarning-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
          index={0} // Index for stacking order
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to add or remove tool categories
      icon: ShapesIcon,
      text: "Tool categories",
      description: "Add, rename, archive, and restore the categories used to organize this space’s tool instances.",
      onClick: () => pushTab(
        <ToolCategoriesTab adminRoute="categories"
          key={`toolCategories-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to edit badge-machine-type relationships, restricted to super admins
      icon: ShieldIcon,
      text: "Badge requirements",
      description: "Manage the credentials required to use each tool. Super admins can add or remove requirements that apply across spaces.",
      onClick: () => pushTab(
        <BadgeMachineTypeWarningTab adminRoute="badgesWarning"
          key={`badgeMachineTypeWarning-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
          index={0} // Index for stacking order
        />
      ),
      disabled: user.isSuperAdmin ? false : true // Disabled if user is not a super admin
    },
    {
      // Option to access space settings
      icon: SettingsIcon,
      text: "Space settings",
      description: "Rename this space, configure check-in steps, manage the badges integration, or delete the space.",
      onClick: () => pushTab(
        <OtherSettingsTab adminRoute="settings"
          key={`otherSettings-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          badgrState={badgrState}
          pushTab={pushTab}
          revert={revert}
          index={2} // Index for stacking order
          onLabChanged={onLabChanged}
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Check-in purposes Page
      icon: ListChecksIcon, //
      text: "Check-in purposes",
      description: "Manage the purposes visitors select during check-in and the typical session duration for each purpose.",
      onClick: () => pushTab(
        <CustomReasonsTab adminRoute="purposes"
          key={`customReasons-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      // Admin tools
      icon: SlidersIcon,
      text: "Admin tools",
      description: "Find users, manage active sessions, and view check-in logs, audit records, and space statistics.",
      onClick: () => pushTab(
        <AdminToolsTab adminRoute="adminTools"
          key={`adminTools-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },

  ] : [
    // Entries when Badgr integration is not enabled
    {
      // Option to manage space admins
      icon: UsersIcon,
      text: "Space admins",
      description: "Add or remove the people who can administer this space.",
      onClick: () => pushTab(
        <ManageUsersTab adminRoute="spaceAdmins"
          key={`manageUsers-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to manage machines
      icon: EspressoMachineIcon,
      text: "Tool instances",
      description: "Add and edit the individual tools in this space, including their names, tool definitions, and categories.",
      onClick: () => pushTab(
        <ManageMachinesTab adminRoute="instances"
          key={`manageMachines-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
          index = {2} // Index for stacking order
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to manage machine types with a warning
      icon: WrenchIcon,
      text: "Tools",
      description: "Add, rename, archive, and restore shared tool definitions. Changes can affect tool instances across spaces.",
      onClick: () => pushTab(
        <MachineTypesWarningTab adminRoute="toolsWarning"
          key={`machineTypesWarning-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
          index={0} // Index for stacking order
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to add or remove tool categories
      icon: ShapesIcon,
      text: "Tool categories",
      description: "Add, rename, archive, and restore the categories used to organize this space’s tool instances.",
      onClick: () => pushTab(
        <ToolCategoriesTab adminRoute="categories"
          key={`toolCategories-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Option to view lab status, opens in a new tab
      icon: ChartIcon,
      text: "Space status",
      description: "Open the space status display in a new tab to see who is checked in and which tools are in use.",
      onClick: () => window.open("/lab_status?lab=" + labId, "_blank"), // Opens the lab status page in a new browser tab
      disabled: false // Option is enabled
    },
    {
      // Option to access space settings
      icon: SettingsIcon,
      text: "Space settings",
      description: "Rename this space, configure check-in steps, manage the badges integration, or delete the space.",
      onClick: () => pushTab(
        <OtherSettingsTab adminRoute="settings"
          key={`otherSettings-${labId}`} // Unique key for the component
          user={user}
          labId={labId}
          badgrState={badgrState}
          pushTab={pushTab}
          revert={revert}
          index={2} // Index for stacking order
          onLabChanged={onLabChanged}
        />
      ),
      disabled: false // Option is enabled
    },
    {
      // Check-in purposes Page
      icon: ListChecksIcon,
      text: "Check-in purposes",
      description: "Manage the purposes visitors select during check-in and the typical session duration for each purpose.",
      onClick: () => pushTab(
        <CustomReasonsTab adminRoute="purposes"
          key={`customReasons-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      // Admin tools
      icon: SlidersIcon,
      text: "Admin tools",
      description: "Find users, manage active sessions, and view check-in logs, audit records, and space statistics.",
      onClick: () => pushTab(
        <AdminToolsTab adminRoute="adminTools"
          key={`adminTools-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },

  ];

  return (
    // Slider component acts as a container for the navigation options
    <Slider 
      name = {`${labId} Settings`} // Name displayed in the slider's navigation bar
      navColor = "rgb(226, 150, 90)"  // Background color of the navigation bar
      slide={true} // Determines if the slider is visible
      index={0} // Index for stacking order
      onClick={()=> revert(1)} // Function to revert one step when navigation bar is clicked
    >
      {/* SliderBranch component renders the list of navigation entries */}
      <SliderBranch entries = {entries} />
    </Slider>
  );
}

// PropTypes for type-checking component props
LabNavigationTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  badgrState: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
  onLabChanged: PropTypes.func,
};

export default LabNavigationTab;
